import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainContainer from "../../components/MainContainer";
import CollectionFilter from "../../components/CollectionFilter/CollectionFilter";
import { useDispatch, useSelector } from "react-redux";
import {
  getExploreCollections,
  getUserFavorites,
} from "../../store/slices/model/asyncThunks";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash.debounce";
import {
  selectCategories,
  selectExploreCollections,
  selectExploreCollectionsFilter,
  selectExploreCollectionsPage,
  selectFavorites,
  selectFavoritesPage,
  setSelectedCollection,
  setSelectedExploreCollectionFilter,
} from "../../store/slices/model/slice";
import Pagination from "../../components/Pagination/Pagination";
import {
  Column,
  Container,
  Content,
  ContentContainer,
  EmptyState,
  PaginationWrapper,
} from "./HomePage.styles";
import VideoMasonrySkeletonLoader from "../../components/utils/VideoMasonrySkeletonLoader";
import ExploreItem from "../../components/ExploreItem/ExploreItem";
import { useMediaQuery } from "react-responsive";
import TermsModal from "../../components/TermsModal/TermsModal";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { setSignUpModalOpen } from "../../store/slices/common/slice";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";

const HomePage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageInStore = useSelector(selectExploreCollectionsPage);
  const pageInStoreFavorite = useSelector(selectFavoritesPage);
  const page = Number(searchParams.get("page")) || pageInStore || 1;
  const search = searchParams.get("search") || "";
  const [searchValue, setSearchValue] = useState(() => search ?? "");
  const filter = useSelector(selectExploreCollectionsFilter);
  const exploreData = useSelector(selectExploreCollections);
  const favoriteData = useSelector(selectFavorites);
  const loggedIn = useSelector(selectLoggedIn);
  const { data: categories } = useSelector(selectCategories);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);

  const isDesktop = useMediaQuery({ maxWidth: 1224 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    if (
      !Number(searchParams.get("page")) &&
      ((filter !== "FAVORITES" && pageInStore) ||
        (filter === "FAVORITES" && pageInStoreFavorite))
    ) {
      if (filter !== "FAVORITES" && pageInStore && pageInStore > 1) {
        setSearchParams({ page: pageInStore });
      }
      if (
        filter === "FAVORITES" &&
        pageInStoreFavorite &&
        pageInStoreFavorite > 1
      ) {
        setSearchParams({ page: pageInStoreFavorite });
      }

      //   else {
      //     setSearchParams({ page: 1 });
      //   }
    }
  }, [filter, pageInStore, pageInStoreFavorite, searchParams, setSearchParams]);

  //   useEffect(() => {
  //     if (!filter) {
  //       setSearchParams({ page: 1 });
  //     }
  //   }, [filter, setSearchParams]);

  useEffect(() => {
    if (categories?.length && !filter) {
      dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
    }
  }, [categories, dispatch, filter]);

  useEffect(() => {
    if (!loggedIn && filter === "FAVORITES" && categories.length) {
      dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
    }
  }, [loggedIn, filter, categories, dispatch]);

  useEffect(() => {
    const hasSeenTerms = localStorage.getItem("hasSeenTerms");
    if (!hasSeenTerms) {
      setIsTermsModalOpen(true);
      localStorage.setItem("hasSeenTerms", "true");
    }
  }, []);

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      default:
        return 5;
    }
  }, [isDesktop, isTable, isMobile]);

  const data = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.data;
    } else {
      return exploreData.data;
    }
  }, [filter, favoriteData, exploreData]);

  const total = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.total;
    } else {
      return exploreData.total;
    }
  }, [filter, favoriteData, exploreData]);

  const loading = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.loading;
    } else {
      return exploreData.loading;
    }
  }, [filter, favoriteData, exploreData]);

  const changeHandler = useCallback(
    (text) => {
      searchParams.set("search", text);
      searchParams.delete("page");
      setSearchParams(searchParams);

      if (search !== text) {
        if (filter !== "FAVORITES") {
          dispatch(
            getExploreCollections({ page: 1, search: text, categoryId: filter })
          );
        } else {
          dispatch(getUserFavorites({ page: 1, search: text }));
        }
      }
    },
    [setSearchParams, searchParams, dispatch, filter, search]
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler]
  );

  const setPage = (page) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });

    setTimeout(() => {
      if (page > 1) {
        searchParams.set("page", page);
      } else {
        searchParams.delete("page");
      }

      setSearchParams(searchParams);
      if (filter !== "FAVORITES") {
        dispatch(getExploreCollections({ page, search, categoryId: filter }));
      } else {
        dispatch(getUserFavorites({ page, search }));
      }
    }, 300);
  };

  useEffect(() => {
    if (filter === "FAVORITES" && !data) {
      searchParams.delete("page");
      setSearchParams(searchParams);
      dispatch(
        getUserFavorites({
          page: 1,
          search: "",
        })
      );
    }
  }, [filter, dispatch, data, searchParams, setSearchParams]);

  useEffect(() => {
    if (!filter || data) return;

    if (filter !== "FAVORITES") {
      searchParams.delete("page");
      setSearchParams(searchParams);

      dispatch(
        getExploreCollections({
          page: 1,
          search: "",
          categoryId: filter,
        })
      );
    }
  }, [dispatch, filter, data, searchParams, setSearchParams]);

  const onChangeFilter = (filter) => {
    dispatch(setSelectedExploreCollectionFilter(filter));

    searchParams.delete("page");
    setSearchParams(searchParams);

    if (filter === "FAVORITES") {
      dispatch(getUserFavorites({ page: 1, search }));
    } else {
      dispatch(getExploreCollections({ page: 1, search, categoryId: filter }));
    }
  };

  return (
    <MainContainer offPadding hideTabs={false}>
      <Container>
        <CollectionFilter
          searchPlaceholderText="title, username or tags"
          filter={filter}
          setFilter={onChangeFilter}
          searchValue={searchValue}
          onSearchChange={(value) => {
            setSearchValue(value);
            debouncedChangeHandler(value);
          }}
        />
        <ContentContainer>
          {loading || !data ? (
            <VideoMasonrySkeletonLoader count={24} />
          ) : (
            <Content>
              {!data.length ? (
                <EmptyState>
                  <h3>No videos found</h3>
                  <p>There are no videos to display at the moment.</p>
                </EmptyState>
              ) : (
                Array.from({ length: numOfColumns }).map((_, colIndex) => {
                  return (
                    <Column key={colIndex}>
                      {data
                        .filter((_, i) => i % numOfColumns === colIndex)
                        .map((item) => {
                          return (
                            <ExploreItem
                              key={item._id}
                              item={item}
                              onItemClick={() => {
                                if (loggedIn) {
                                  dispatch(setSelectedCollection(item));
                                  setIsGenerateCollectionModalOpen(true);
                                } else {
                                  dispatch(setSignUpModalOpen(true));
                                }
                              }}
                            />
                          );
                        })}
                    </Column>
                  );
                })
              )}
            </Content>
          )}
        </ContentContainer>
        <PaginationWrapper>
          <Pagination
            currentPage={page}
            totalCount={total}
            perPage={20}
            onPageChange={(page) => setPage(page)}
          />
        </PaginationWrapper>
      </Container>

      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
        onChangeFilter={onChangeFilter}
      />
    </MainContainer>
  );
};

export default HomePage;
